import qs from 'qs'
import React, { useCallback, useContext, useMemo } from 'react'

import { AddDetailsCopy } from './Common'
import { PlaceInputTrigger } from './Common/SearchFields'
import SearchOrSeparator from './SearchOrSeparator'
import SearchPlace from './SearchPlace'
import CreateTripItem from '../Create/index'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useCruiseSearchEnabled } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import { useTripMetadataPrefetch } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
  sectionId?: string
}

const DEFAULT_SUB_TITLE = 'Search by destination'

const SearchTourOrCruise = ({ tripId, sectionId }: Props) => {
  useTripMetadataPrefetch({ tripId })
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()
  const isCruiseSearchEnabled = useCruiseSearchEnabled()

  const { searchItem } = useContext(GlobalSearchStateContext)
  const source = useTripClientSource()
  const query = useMemo(
    () =>
      qs.stringify({
        destinationId: searchItem
          ? searchItem.value
          : ANYWHERE_SEARCH_ITEM.value,
        destinationName: searchItem
          ? searchItem.format.mainText
          : ANYWHERE_SEARCH_ITEM.format.mainText,
        source,
      }),
    [searchItem, source],
  )

  const onPrimaryActionClick = useCallback(() => {
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, sectionId, tripId])

  const tourV2SearchEnabled = useTourV2SearchEnabled()

  return (
    <>
      <ModalHeaderWrapper
        title={isCruiseSearchEnabled ? 'Tours' : 'Tours & cruises'}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="medium">
              Find the world’s best escapes, handpicked by experts
            </BodyText>
            {tourV2SearchEnabled && (
              <>
                <PlaceInputTrigger
                  subTitle={searchItem?.format.mainText ?? DEFAULT_SUB_TITLE}
                  onClick={() =>
                    openModal(
                      SearchPlace,
                      {
                        searchView: 'TOUR_AND_CRUISES' as const,
                        tripId,
                      },
                      {
                        height: 'full',
                      },
                    )
                  }
                />
                <TextButton
                  kind="primary"
                  fit="flex"
                  to={'/search/tours?' + query}
                  onClick={onPrimaryActionClick}
                  size="large"
                >
                  Search
                </TextButton>
              </>
            )}
            {!tourV2SearchEnabled && (
              <TextButton
                kind="primary"
                fit="full-width"
                to="/tours"
                onClick={onPrimaryActionClick}
                size="large"
              >
                View offers
              </TextButton>
            )}
            <SearchOrSeparator />
            <AddDetailsCopy>
              <Group
                direction="horizontal"
                horizontalAlign="space-between"
                verticalAlign="center"
              >
                <BodyText variant="medium" align="center">
                  Have you already booked elsewhere?
                </BodyText>
                <TextButton
                  kind="secondary"
                  variant="dark"
                  onClick={() =>
                    openModal(CreateTripItem, {
                      itemType: 'TOUR_AND_CRUISES' as const,
                      tripId,
                    })
                  }
                >
                  Add booking to trip
                </TextButton>
              </Group>
            </AddDetailsCopy>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchTourOrCruise
